import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../../components/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function RegisterPage() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        company_name: '',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await register({
                ...formData,
                role: 'customer',
            });
            toast.success('Account created successfully!');
            navigate('/customer-details');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-surface-app flex">
            {/* Left side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ 
                        backgroundImage: 'url(https://images.unsplash.com/photo-1746468222089-92bfc5546cd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHJlZCUyMGJsYWNrJTIwZGlnaXRhbCUyMG5ldHdvcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MDczMTAxNHww&ixlib=rb-4.1.0&q=85)'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-surface-app via-surface-app/80 to-brand-red/20" />
                <div className="relative z-10 flex flex-col justify-center px-16">
                    <Logo size="large" className="mb-8" />
                    <h1 className="text-5xl font-heading font-bold text-white leading-tight mb-6">
                        Start Your ABM<br />
                        <span className="text-brand-red">Journey Today</span>
                    </h1>
                    <p className="text-lg text-content-secondary max-w-md">
                        Join companies leveraging Account-Based Marketing to drive pipeline and revenue growth.
                    </p>

                    <div className="mt-12 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-brand-red/20 flex items-center justify-center">
                                <span className="text-brand-red font-bold">1</span>
                            </div>
                            <span className="text-content-secondary">Create your account</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-brand-red/20 flex items-center justify-center">
                                <span className="text-brand-red font-bold">2</span>
                            </div>
                            <span className="text-content-secondary">Complete company details</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-brand-red/20 flex items-center justify-center">
                                <span className="text-brand-red font-bold">3</span>
                            </div>
                            <span className="text-content-secondary">Upload documents & assets</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-brand-red/20 flex items-center justify-center">
                                <span className="text-brand-red font-bold">4</span>
                            </div>
                            <span className="text-content-secondary">Get approved & go live</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right side - Register Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-8">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8">
                        <Logo size="large" />
                    </div>

                    <h2 className="text-3xl font-heading font-bold text-white mb-2">
                        Create Account
                    </h2>
                    <p className="text-content-secondary mb-8">
                        Get started with your ABM dashboard
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-content-secondary">
                                Full Name
                            </Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="John Smith"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="input-base h-12"
                                data-testid="register-name-input"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="company" className="text-content-secondary">
                                Company Name
                            </Label>
                            <Input
                                id="company"
                                type="text"
                                placeholder="Your Company"
                                value={formData.company_name}
                                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                className="input-base h-12"
                                data-testid="register-company-input"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-content-secondary">
                                Work Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@company.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="input-base h-12"
                                data-testid="register-email-input"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-content-secondary">
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Create a strong password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="input-base h-12 pr-12"
                                    data-testid="register-password-input"
                                    required
                                    minLength={6}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-content-muted hover:text-white"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="btn-primary w-full h-12 text-base"
                            disabled={loading}
                            data-testid="register-submit-btn"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Creating account...
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    Create Account
                                    <ArrowRight className="w-5 h-5" />
                                </span>
                            )}
                        </Button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-content-secondary">
                            Already have an account?{' '}
                            <Link 
                                to="/login" 
                                className="text-brand-red hover:text-brand-red-hover font-medium"
                                data-testid="login-link"
                            >
                                Sign In
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
